import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowUpDown,
  Box,
  Check,
  ClipboardList,
  Info,
  Layers,
  ListPlus,
  MapPin,
  Package,
  RefreshCcw,
  Save,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';
import {
  fetchAllPaginated,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';
import { normalizeBrandLabel } from '../utils/brandFilters';
import { useSite } from '../context/SiteContext';
import { applyActiveProductsFilter } from '../utils/productStatus';
import { applyStockDelta, isVariantTracked, listStockVariantOptions } from '../utils/variantStock';
import LotesAjustePanel from '../components/productos/LotesAjustePanel';
import { formatDateOnlyValue, getLocalDateKey } from '../utils/localDateTime';
import {
  addDaysYmd,
  expiryAlertDays,
  fetchLotesProducto,
  moverStockConLotes,
  preselectLotesSalida,
  productTracksExpiry,
  toLotePayload,
  validateLotesEntrada,
} from '../utils/stockLotes';

// Estado del panel de lotes (vencimientos). Ver components/productos/LotesAjustePanel.
const LOTES_AJUSTE_INICIAL = {
  porVencimiento: false,
  entrada: { fecha_vencimiento: '', lote_codigo: '' },
  salidas: {},
  manual: false,
};

const formatCantidad = (value) => Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const InventarioAjuste = ({ onNavigate }) => {
  const { profile, organization } = useAuth();
  // ✅ FIX colateral multi-sucursal: arrancamos con la sucursal del SiteContext y
  // re-sincronizamos cuando el usuario cambia desde el Topbar.
  const { selectedSiteId: ctxSelectedSiteId } = useSite();

  const formRef = useRef(null);
  const dropdownRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [defaultSiteId, setDefaultSiteId] = useState('');

  const [productos, setProductos] = useState([]);
  const [sitios, setSitios] = useState([]);

  const [selectedProd, setSelectedProd] = useState(null);
  // Talles y colores: en productos con stock por talle se ajusta una variante.
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [selectedSitioId, setSelectedSitioId] = useState(ctxSelectedSiteId || '');

  // ✅ FIX colateral: re-aplicamos el cambio de sucursal del Topbar.
  useEffect(() => {
    if (ctxSelectedSiteId && ctxSelectedSiteId !== selectedSitioId) {
      setSelectedSitioId(ctxSelectedSiteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxSelectedSiteId]);
  const [tipo, setTipo] = useState('entrada');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  // Vencimientos y lotes
  const [lotesProd, setLotesProd] = useState([]);
  const [lotesLoading, setLotesLoading] = useState(false);
  const [lotesAjuste, setLotesAjuste] = useState(LOTES_AJUSTE_INICIAL);

  const [prodQuery, setProdQuery] = useState('');
  const [prodDropdownOpen, setProdDropdownOpen] = useState(false);

  useEffect(() => {
    if (profile?.organization_id) fetchMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (selectedSitioId) persistSelectedSiteId(selectedSitioId);
  }, [selectedSitioId]);

  useEffect(() => {
    const handler = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) setProdDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchMasters = async () => {
    try {
      const orgId = profile.organization_id;
      // resOrg sale del AuthContext (no se refetchea)
      const resOrg = { data: organization };
      const resSitios = await supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre');

      const availableSites = resSitios.data || [];
      const resolvedDefaultSiteId = resolvePreferredStockSiteId(resOrg.data, availableSites);

      const legacyStockResult = await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId: resolvedDefaultSiteId,
      });

      // Criterio unico de producto operable: ninguna de las dos columnas de
      // estado en false y sin archivar. Antes miraba solo `active`, asi que un
      // producto dado de baja por el importador (que escribe `is_active`)
      // seguia apareciendo para ajustar stock.
      // Talles y colores: variantes y stock por talle. Si la consulta extendida
      // falla (esquema no aplicado en esta base) se usa la de siempre.
      const fetchProductos = (variantSelect) => fetchAllPaginated(() =>
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select(`*, stock_levels(*), ${variantSelect}`)
          .eq('organization_id', orgId)
        ).order('name', { ascending: true })
      );
      let productosData;
      try {
        productosData = await fetchProductos('product_variants(id, stock_quantity, talle, talle_orden, color_id, activo, archivado_at, attributes, colores(id, nombre, hex)), stock_variantes(variant_id, site_id, quantity)');
      } catch (variantSelectError) {
        console.error('Ajuste: catálogo con talles no disponible, uso el clásico:', variantSelectError);
        productosData = await fetchProductos('product_variants(stock_quantity)');
      }

      setProductos(productosData || []);
      setSitios(availableSites);
      setDefaultSiteId(resolvedDefaultSiteId || '');
      setSelectedSitioId((prev) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: prev,
        }),
      );

      if (legacyStockResult.inserted > 0) {
        toast.success(`Se recuperó el stock importado en ${legacyStockResult.inserted} productos.`);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    }
  };

  const resetForm = () => {
    setSelectedProd(null);
    setSelectedVariantId('');
    setSelectedSitioId(
      resolvePreferredSiteId(sitios, {
        currentSiteId: selectedSitioId,
      }),
    );
    setTipo('entrada');
    setCantidad('');
    setMotivo('');
    setLotesAjuste(LOTES_AJUSTE_INICIAL);
    setLotesProd([]);
    setProdQuery('');
    setProdDropdownOpen(false);
    setIsSuccess(false);
  };

  const sitioNombre = useMemo(
    () => sitios.find((site) => site.id === selectedSitioId)?.nombre || '-',
    [sitios, selectedSitioId]
  );

  const cantidadNumerica = Number(cantidad || 0);
  const porTalle = isVariantTracked(selectedProd);
  const variantOptions = useMemo(
    () => listStockVariantOptions(selectedProd, selectedSitioId || defaultSiteId || null),
    [defaultSiteId, selectedProd, selectedSitioId]
  );
  const selectedVariantOption = variantOptions.find((option) => option.id === selectedVariantId) || null;
  const porLotes = !porTalle && productTracksExpiry(organization, selectedProd);
  const alertDays = expiryAlertDays(organization);

  const cargarLotes = async () => {
    if (!porLotes || !selectedProd?.id || !selectedSitioId) { setLotesProd([]); return; }
    setLotesLoading(true);
    try {
      setLotesProd(await fetchLotesProducto({ supabase, productId: selectedProd.id, siteId: selectedSitioId }));
    } catch (error) {
      console.error('Ajuste: no se pudieron leer los lotes', error);
      setLotesProd([]);
    } finally {
      setLotesLoading(false);
    }
  };

  useEffect(() => {
    cargarLotes();
    // Producto nuevo: vencimiento sugerido por vida útil, selección de lotes en cero.
    setLotesAjuste({
      ...LOTES_AJUSTE_INICIAL,
      entrada: {
        fecha_vencimiento: selectedProd?.vida_util_dias ? addDaysYmd(getLocalDateKey(), selectedProd.vida_util_dias) : '',
        lote_codigo: '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porLotes, selectedProd?.id, selectedSitioId]);

  // Preselección de lotes que salen: FEFO, o solo vencidos si es baja por
  // vencimiento. Si el usuario tocó una cantidad, se respeta lo que eligió.
  useEffect(() => {
    if (!porLotes || tipo !== 'salida' || lotesAjuste.manual) return;
    const sel = preselectLotesSalida({ lotes: lotesProd, cantidad: Number(cantidad || 0), porVencimiento: lotesAjuste.porVencimiento });
    setLotesAjuste((actual) => ({ ...actual, salidas: Object.fromEntries(sel.map((x) => [x.lote_id, x.cantidad])) }));
  }, [porLotes, tipo, cantidad, lotesProd, lotesAjuste.manual, lotesAjuste.porVencimiento]);
  const stockActual = useMemo(() => {
    if (!selectedProd) return 0;
    if (porTalle) return selectedVariantOption ? selectedVariantOption.stock : 0;
    return getProductStockForSite(selectedProd, selectedSitioId || defaultSiteId || null);
  }, [defaultSiteId, porTalle, selectedProd, selectedSitioId, selectedVariantOption]);

  const stockResultante = selectedProd
    ? stockActual + (tipo === 'entrada' ? cantidadNumerica : -cantidadNumerica)
    : 0;

  const estadoOperacion = useMemo(() => {
    if (isSuccess) return { label: 'Registrado', tone: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (saving) return { label: 'Procesando', tone: 'text-blue-700', bg: 'bg-blue-50' };
    if (tipo === 'salida' && cantidadNumerica > stockActual) {
      return { label: 'Sin stock suficiente', tone: 'text-rose-700', bg: 'bg-rose-50' };
    }
    if (!selectedProd || !selectedSitioId || !cantidadNumerica || (porTalle && !selectedVariantOption)) {
      return { label: 'Incompleto', tone: 'text-slate-600', bg: 'bg-slate-50' };
    }
    return { label: 'Listo', tone: 'text-amber-700', bg: 'bg-amber-50' };
  }, [cantidadNumerica, isSuccess, porTalle, saving, selectedProd, selectedSitioId, selectedVariantOption, stockActual, tipo]);

  const canSubmit = Boolean(
    selectedProd
    && selectedSitioId
    && (!porTalle || selectedVariantOption)
    && cantidadNumerica > 0
    && (tipo === 'entrada' || cantidadNumerica <= stockActual)
  );

  const productosFiltrados = useMemo(() => {
    const term = prodQuery.trim().toLowerCase();
    if (!term) return productos.slice(0, 40);
    return productos
      .filter((product) => {
        const name = (product.name || '').toLowerCase();
        const barcode = (product.barcode || '').toLowerCase();
        const brand = normalizeBrandLabel(product.brand, '').toLowerCase();
        return name.includes(term) || barcode.includes(term) || brand.includes(term);
      })
      .slice(0, 60);
  }, [productos, prodQuery]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || isSuccess) return;

    if (!selectedProd || !selectedSitioId || cantidadNumerica <= 0) {
      toast.error('Completá producto, sucursal y una cantidad válida');
      return;
    }

    if (porTalle && !selectedVariantOption) {
      toast.error('Elegí el talle/color a ajustar');
      return;
    }

    setSaving(true);
    try {
      const orgId = profile.organization_id;
      const cantFinal = tipo === 'entrada' ? cantidadNumerica : -cantidadNumerica;

      // Talles y colores: el ajuste de una variante va por fn_mover_stock
      // (lock de fila, control de stock y kardex con variant_id en la base).
      if (porTalle) {
        const motivoTalle = `${motivo?.trim() ? `Ajuste manual: ${motivo.trim()}` : 'Ajuste manual'} (${selectedVariantOption.label})`;
        await applyStockDelta({
          supabase,
          organizationId: orgId,
          productId: selectedProd.id,
          variantId: selectedVariantOption.id,
          siteId: selectedSitioId,
          delta: cantFinal,
          tipo: tipo === 'entrada' ? 'Ingreso' : 'Egreso',
          motivo: motivoTalle,
          allowNegative: false,
        });
        setIsSuccess(true);
        toast.success('Ajuste registrado');
        await fetchMasters();
        setTimeout(() => {
          resetForm();
        }, 1600);
        return;
      }

      // Vencimientos y lotes: el ajuste va por la base con el lote dirigido
      // (stock + kardex + lotes en una sola transacción).
      if (porLotes) {
        const motivoBase = motivo?.trim() ? `Ajuste manual: ${motivo.trim()}` : 'Ajuste manual';
        let lotesPayload = [];
        let refTipo = 'ajuste';
        let motivoLotes = motivoBase;
        if (tipo === 'entrada') {
          const lote = { ...lotesAjuste.entrada, cantidad: cantidadNumerica };
          const v = validateLotesEntrada([lote], cantidadNumerica);
          if (!v.ok) { toast.error(v.error); return; }
          if (v.vencidos.length > 0
            && !window.confirm(`La fecha ${formatDateOnlyValue(lote.fecha_vencimiento)} ya pasó: el lote entra vencido. ¿Registrar igual?`)) {
            return;
          }
          lotesPayload = [toLotePayload(lote)];
        } else {
          const disponibles = new Map(lotesProd.map((l) => [l.id, Number(l.cantidad_actual) || 0]));
          lotesPayload = Object.entries(lotesAjuste.salidas || {})
            .map(([lote_id, cant]) => ({ lote_id, cantidad: Number(cant) || 0 }))
            .filter((x) => x.cantidad > 0);
          const suma = lotesPayload.reduce((a, x) => a + x.cantidad, 0);
          if (lotesPayload.some((x) => x.cantidad > (disponibles.get(x.lote_id) ?? 0) + 1e-9)) {
            toast.error('Un lote no tiene esa cantidad. Revisá los lotes elegidos.');
            return;
          }
          if (suma > cantidadNumerica + 1e-9) {
            toast.error('Los lotes elegidos superan la cantidad del egreso.');
            return;
          }
          if (lotesAjuste.porVencimiento) {
            if (Math.abs(suma - cantidadNumerica) > 1e-9) {
              toast.error(`La baja por vencimiento tiene que salir de lotes vencidos: elegiste ${formatCantidad(suma)} de ${formatCantidad(cantidadNumerica)}.`);
              return;
            }
            refTipo = 'baja_vencimiento';
            motivoLotes = motivo?.trim() ? `VENCIMIENTO: ${motivo.trim()}` : 'VENCIMIENTO';
          }
        }
        await moverStockConLotes({
          supabase,
          organizationId: orgId,
          productId: selectedProd.id,
          siteId: selectedSitioId,
          delta: cantFinal,
          tipo: tipo === 'entrada' ? 'Ingreso' : 'Egreso',
          motivo: motivoLotes,
          lotes: lotesPayload,
          origen: 'ajuste',
          refTipo,
          allowNegative: false,
        });
        setIsSuccess(true);
        toast.success('Ajuste registrado');
        await fetchMasters();
        setTimeout(() => {
          resetForm();
        }, 1600);
        return;
      }

      const { data: level, error: levelError } = await supabase
        .from('stock_levels')
        .select('id, quantity')
        .eq('product_id', selectedProd.id)
        .eq('site_id', selectedSitioId)
        .maybeSingle();

      if (levelError) throw levelError;

      const stockBase = Number(level?.quantity || 0);
      if (tipo === 'salida' && cantidadNumerica > stockBase) {
        toast.error(`Stock insuficiente. Disponible en ${sitioNombre}: ${formatCantidad(stockBase)}`);
        return;
      }

      if (level?.id) {
        const { error: upError } = await supabase
          .from('stock_levels')
          .update({
            quantity: stockBase + cantFinal,
            updated_at: new Date(),
          })
          .eq('id', level.id);

        if (upError) throw upError;
      } else {
        const { error: insError } = await supabase
          .from('stock_levels')
          .insert([{
            organization_id: orgId,
            product_id: selectedProd.id,
            site_id: selectedSitioId,
            quantity: cantFinal,
          }]);

        if (insError) throw insError;
      }

      const motivoFinal = motivo?.trim()
        ? `Ajuste manual: ${motivo.trim()}`
        : 'Ajuste manual';

      const { error: movementError } = await supabase.from('movimientos_stock').insert([
        {
          organization_id: orgId,
          producto_id: selectedProd.id,
          tipo: tipo === 'entrada' ? 'Ingreso' : 'Egreso',
          cantidad: cantidadNumerica,
          motivo: motivoFinal,
          sitio_origen: tipo === 'salida' ? selectedSitioId : null,
          sitio_destino: tipo === 'entrada' ? selectedSitioId : null,
        },
      ]);
      if (movementError) {
        console.error('No se pudo registrar el movimiento del ajuste', movementError);
      }

      setIsSuccess(true);
      toast.success('Ajuste registrado');
      await fetchMasters();

      setTimeout(() => {
        resetForm();
      }, 1600);
    } catch (error) {
      console.error('Error ajuste:', error);
      toast.error((porTalle || porLotes) && error?.message ? error.message : 'Error al procesar el ajuste');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ArrowUpDown size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Ajuste de stock
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Entrada y salida de mercadería por ajuste
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => (typeof onNavigate === 'function'
              ? onNavigate('inventario-ajuste-masivo')
              : window.location.assign('/sistema/inventario-ajuste-masivo'))}
            className="px-5 py-2.5 rounded-2xl bg-blue-50 border border-blue-100 text-[#51A7FD] shadow-sm hover:bg-blue-100 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ListPlus size={14} /> Ajuste masivo
          </button>

          <button
            type="button"
            onClick={resetForm}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <X size={14} /> Cancelar
          </button>

          {/* Prueba de ajuste registrado para el recorrido guiado: el clic no
              alcanza (el movimiento puede fallar por stock o sucursal). */}
          {isSuccess && (
            <span data-onboarding-id="stock-adjust-success" className="sr-only">
              Ajuste registrado
            </span>
          )}
          <button
            type="button"
            data-onboarding-id="stock-adjust-submit"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!canSubmit || saving || isSuccess}
            className={`px-5 py-2.5 rounded-2xl shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
              isSuccess ? 'bg-[#3ff2aa] text-[#0B2A36]' : 'bg-[#0B2A36] text-white hover:opacity-95'
            }`}
          >
            {isSuccess ? (
              <span className="inline-flex items-center gap-2 animate-check-in">
                <Check size={16} strokeWidth={3} /> Confirmado
              </span>
            ) : saving ? (
              <>
                <RefreshCcw size={16} className="animate-spin" /> Procesando
              </>
            ) : (
              <>
                <Save size={16} strokeWidth={3} className="text-[#3FF2AA]" /> Confirmar
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-50 text-[#51A7FD] shadow-inner shrink-0">
            <Package size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Producto</p>
            <div className="text-[12px] font-black text-slate-800 uppercase tracking-tight truncate">
              {selectedProd?.name || '—'}
            </div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">
              {selectedProd
                ? `${normalizeBrandLabel(selectedProd.brand)}${selectedProd.barcode ? ` · ${selectedProd.barcode}` : ''}`
                : 'Sin código'}
            </div>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3 min-w-0">
          <div className="p-2.5 rounded-xl bg-slate-50 text-slate-500 shadow-inner shrink-0">
            <MapPin size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Sucursal</p>
            <div className="text-[12px] font-black text-slate-800 uppercase tracking-tight truncate">{sitioNombre}</div>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${stockActual > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'} shadow-inner shrink-0`}>
            <Package size={20} />
          </div>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Stock actual</p>
            <h3 className="text-[14px] font-black tracking-tight text-slate-800">
              {formatCantidad(stockActual)} <span className="text-[9px] text-slate-400 font-bold">{selectedProd?.unit_of_measure || 'un'}</span>
            </h3>
          </div>
        </div>

        <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${estadoOperacion.bg} ${estadoOperacion.tone} shadow-inner shrink-0`}>
            <Layers size={20} />
          </div>
          <div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Estado</p>
            <h3 className={`text-[14px] font-black tracking-tight ${estadoOperacion.tone}`}>{estadoOperacion.label}</h3>
          </div>
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <Box size={14} className="text-[#51A7FD]" /> Datos del ajuste
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1" ref={dropdownRef}>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <Search size={10} /> Producto
                </label>

                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                  <input
                    type="text"
                    data-onboarding-id="stock-adjust-product-field"
                    value={selectedProd ? `${selectedProd.name} · ${normalizeBrandLabel(selectedProd.brand)}${selectedProd.barcode ? ` [${selectedProd.barcode}]` : ''}` : prodQuery}
                    onChange={(event) => {
                      setSelectedProd(null);
                      setProdQuery(event.target.value);
                      setProdDropdownOpen(true);
                    }}
                    onFocus={() => setProdDropdownOpen(true)}
                    placeholder="BUSCAR NOMBRE, MARCA O CÓDIGO..."
                    className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase transition-all"
                  />

                  {(selectedProd || prodQuery) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedProd(null);
                        setProdQuery('');
                        setProdDropdownOpen(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      title="Limpiar"
                    >
                      <X size={14} />
                    </button>
                  )}

                  {prodDropdownOpen && productosFiltrados.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                      {productosFiltrados.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => {
                            setSelectedProd(product);
                            setSelectedVariantId('');
                            setProdQuery('');
                            setProdDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-all border-b border-slate-50 last:border-0"
                        >
                          <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 leading-none flex items-center justify-between gap-3">
                            <span className="truncate">{product.name}</span>
                            <span className="shrink-0 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100 text-[8px] font-black uppercase tracking-widest">
                              {normalizeBrandLabel(product.brand)}
                            </span>
                          </div>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-1 flex justify-between gap-3">
                            <span>{product.barcode || 'S/COD'}</span>
                            <span className="text-[#51A7FD]">
                              {formatCantidad(getProductStockForSite(product, selectedSitioId || defaultSiteId || null))} {product.unit_of_measure || 'un'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedProd && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-blue-50/50 rounded-xl border border-blue-100">
                    <span className="inline-flex items-center gap-2 text-[9px] font-black text-blue-600 uppercase tracking-tight">
                      <Info size={14} className="text-[#51A7FD]" />
                      Marca: {normalizeBrandLabel(selectedProd.brand)} · Tipo: {selectedProd.product_type || '—'} · U.M.: {selectedProd.unit_of_measure || 'un'}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${stockActual > 0 ? 'bg-white text-emerald-700 border-emerald-100' : 'bg-white text-rose-700 border-rose-100'}`}>
                      Stock: {formatCantidad(stockActual)}
                    </span>
                  </div>
                )}

                {selectedProd && porTalle && (
                  <div className="mt-2 space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Talle / color</label>
                    {variantOptions.length === 0 ? (
                      <p className="text-[10px] font-bold text-rose-600 px-1">Este producto no tiene talles activos. Definilos desde el detalle del producto.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {variantOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setSelectedVariantId(option.id)}
                            className={`px-2.5 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-tight transition-all ${
                              selectedVariantId === option.id
                                ? 'bg-[#0B2A36] text-white border-[#0B2A36]'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-[#51A7FD]'
                            }`}
                          >
                            {option.label}
                            <span className={`ml-1.5 ${selectedVariantId === option.id ? 'text-[#3FF2AA]' : option.stock > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                              {formatCantidad(option.stock)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                  <MapPin size={10} /> Sucursal
                </label>
                <select
                  required
                  data-onboarding-id="stock-adjust-site-field"
                  value={selectedSitioId}
                  onChange={(event) => setSelectedSitioId(event.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner transition-all cursor-pointer"
                >
                  <option value="">Seleccionar...</option>
                  {sitios.map((site) => (
                    <option key={site.id} value={site.id}>{site.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4 space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Movimiento</label>
                  <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                    <button
                      type="button"
                      data-onboarding-id="stock-adjust-type-in"
                      onClick={() => setTipo('entrada')}
                      className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase transition-all flex items-center justify-center gap-1 ${
                        tipo === 'entrada' ? 'bg-white text-emerald-700 shadow-sm border border-slate-100' : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      <ArrowUpDown size={12} /> Ingreso
                    </button>
                    <button
                      type="button"
                      onClick={() => setTipo('salida')}
                      className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase transition-all flex items-center justify-center gap-1 ${
                        tipo === 'salida' ? 'bg-white text-rose-700 shadow-sm border border-slate-100' : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      <ArrowUpDown size={12} className="rotate-180" /> Egreso
                    </button>
                  </div>
                </div>

                <div className="md:col-span-3 space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Cantidad</label>
                  <input
                    type="number"
                    data-onboarding-id="stock-adjust-qty-field"
                    onFocus={(e) => e.target.select()}
                    step="0.01"
                    min="0"
                    required
                    value={cantidad}
                    onChange={(event) => setCantidad(event.target.value)}
                    placeholder="0.00"
                    className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-black text-slate-800 outline-none focus:border-[#51A7FD] shadow-inner text-right"
                  />
                </div>

                <div className="md:col-span-5 space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Motivo / Ref</label>
                  <input
                    type="text"
                    value={motivo}
                    onChange={(event) => setMotivo(event.target.value.toUpperCase())}
                    placeholder="EJ: DIFERENCIA DE STOCK..."
                    className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner"
                  />
                </div>
              </div>

              {porLotes && selectedSitioId && (
                <LotesAjustePanel
                  tipo={tipo}
                  cantidad={cantidadNumerica}
                  lotes={lotesProd}
                  loading={lotesLoading}
                  value={lotesAjuste}
                  onChange={setLotesAjuste}
                  alertDays={alertDays}
                />
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm h-full flex flex-col">
            <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <ClipboardList size={14} className="text-slate-400" /> Resumen operativo
            </h2>

            <div className="flex-1 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 space-y-4 shadow-inner">
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Tipo</span>
                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${
                  tipo === 'entrada' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'
                }`}>
                  {tipo === 'entrada' ? 'Ingreso (+)' : 'Egreso (-)'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cantidad</span>
                <span className="text-[14px] font-black text-slate-800">
                  {formatCantidad(cantidadNumerica)} <span className="text-[9px] text-slate-400 font-bold">{selectedProd?.unit_of_measure || 'un'}</span>
                </span>
              </div>

              <div className="h-px bg-slate-200" />

              <div className="space-y-2">
                <div className="flex justify-between items-center gap-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Stock actual</span>
                  <span className="text-[10px] font-bold text-slate-700 uppercase text-right">
                    {formatCantidad(stockActual)} {selectedProd?.unit_of_measure || 'un'}
                  </span>
                </div>

                <div className="flex justify-between items-center gap-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Stock resultante</span>
                  <span className={`text-[10px] font-black uppercase text-right ${stockResultante < 0 ? 'text-rose-600' : 'text-slate-700'}`}>
                    {formatCantidad(stockResultante)} {selectedProd?.unit_of_measure || 'un'}
                  </span>
                </div>

                <div className="flex justify-between items-center gap-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sucursal</span>
                  <span className="text-[10px] font-bold text-slate-700 uppercase truncate text-right">{sitioNombre}</span>
                </div>

                <div className="flex justify-between items-center gap-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Motivo</span>
                  <span className="text-[10px] font-bold text-slate-500 uppercase truncate text-right">{motivo || '—'}</span>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-3 space-y-2">
              {tipo === 'salida' && cantidadNumerica > stockActual && (
                <div className="flex gap-2 p-3 bg-rose-50 rounded-xl border border-rose-100">
                  <Info size={14} className="text-rose-500 shrink-0 mt-0.5" />
                  <p className="text-[9px] font-bold text-rose-600 uppercase leading-tight tracking-wide">
                    La salida no puede superar el stock disponible en la sucursal elegida.
                  </p>
                </div>
              )}

              <div className="flex gap-2 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                <Info size={14} className="text-[#51A7FD] shrink-0 mt-0.5" />
                <p className="text-[9px] font-bold text-blue-600 uppercase leading-tight tracking-wide">
                  Al confirmar, se actualiza el stock del sitio elegido de forma inmediata.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <style>{`
        .animate-check-in { animation: checkIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes checkIn {
          from { transform: scale(0.92); opacity: 0.6; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InventarioAjuste;
